import type { schema } from "@loanlink-nl/zero";
import type { Row } from "@rocicorp/zero";
import { refDefault, useArrayFilter, useSorted } from "@vueuse/core";
import type { Ref } from "vue";
import { computed, ref } from "vue";

type Doc = Row<(typeof schema)["tables"]["hdnRequestedDocument"]>;

export type DossierDocument = Row<(typeof schema)["tables"]["dossierDocument"]>;

export type HdnRequestedDocument = Doc & {
	ketenpartijStatus?: Row<
		(typeof schema)["tables"]["hdnRequestedDocumentStatusUpdate"]
	>;
	documentStatus?: Row<
		(typeof schema)["tables"]["hdnRequestedDocumentStatusUpdate"]
	>;
	sentDocumentAction?: Row<
		(typeof schema)["tables"]["hdnRequestedDocumentAction"]
	> & {
		document?: Row<(typeof schema)["tables"]["dossierDocument"]>;
	};
	linkedDocumentAction?: Row<
		(typeof schema)["tables"]["hdnRequestedDocumentAction"]
	> & {
		document?: Row<(typeof schema)["tables"]["dossierDocument"]>;
	};
	partijNawData?: Row<(typeof schema)["tables"]["hdnPartijNawData"]>;
};

export const filterOpts = {
	toSubmit: "Aan te leveren",
	submitted: "Aangeleverd",
	expired: "Vervallen",
	all: "Alle",
};

const statusOrder = [
	"07 Afgekeurd en opnieuw aan te leveren (zie toelichting)",
	"01 Aan te leveren",
	"02 In behandeling",
	"03 Ontvangen",
	"06 Voorlopig akkoord",
	"04 Akkoord",
	"08 Vervallen (zie toelichting)",
] as const;
const statusOrderMap = statusOrder.reduce(
	(acc, status, index) => {
		acc[status] = index;
		return acc;
	},
	{} as Record<string, number>,
);

const statusFilterMap = {
	all: () => true,
	toSubmit: (status?: string) =>
		status &&
		[
			"01 Aan te leveren",
			"07 Afgekeurd en opnieuw aan te leveren (zie toelichting)",
		].includes(status),
	submitted: (status?: string) =>
		status &&
		[
			"02 In behandeling",
			"03 Ontvangen",
			"04 Akkoord",
			"06 Voorlopig akkoord",
		].includes(status),
	expired: (status?: string) => status === "08 Vervallen (zie toelichting)",
};

export function getStatus(doc: HdnRequestedDocument) {
	if (doc.ketenpartijStatus) {
		return doc.ketenpartijStatus;
	}

	return doc.documentStatus;
}

export function useLoanApplicationSortedDocuments(
	docs: Ref<HdnRequestedDocument[] | null>,
) {
	const selectedFilter = ref<keyof typeof filterOpts>("toSubmit");

	const filterOptsDisplay = computed(() => {
		if (!docs.value) {
			return filterOpts;
		}

		const filterOptions: Record<keyof typeof filterOpts, string> = {
			all: getLabelWithQty("all"),
			toSubmit: getLabelWithQty("toSubmit"),
			submitted: getLabelWithQty("submitted"),
			expired: getLabelWithQty("expired"),
		};

		return filterOptions;

		function getLabelWithQty(key: keyof typeof filterOpts) {
			const labelWithQty = `${filterOpts[key]} (${docs.value?.filter((doc) => statusFilterMap[key](getStatus(doc)?.status)).length})`;
			return labelWithQty;
		}
	});

	const sortedDocuments = useSorted(
		useArrayFilter(refDefault(docs, []), (doc) =>
			statusFilterMap[selectedFilter.value](getStatus(doc)?.status),
		),
		(a, b) => {
			const aStatus = getStatus(a)?.status;
			const bStatus = getStatus(b)?.status;
			return aStatus && bStatus
				? (statusOrderMap[aStatus] ?? 0) - (statusOrderMap[bStatus] ?? 0)
				: 0;
		},
	);

	return { sortedDocuments, filterOptsDisplay, selectedFilter };
}
